export const getCssVar = (name) => {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
};

// Color conversion helpers — reusable across app (moved from BrandingProvider for DRY)
export const hexToHSL = (hex) => {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
};

const hslToHex = (h, s, l) => {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

export const adjustLightness = (hex, amount) => {
  const { h, s, l } = hexToHSL(hex);
  return hslToHex(h, s, Math.max(0, Math.min(100, l + amount)));
};

export const deriveDarkPalette = (primaryHex) => {
  const { h, s } = hexToHSL(primaryHex);
  return {
    bg: hslToHex(h, Math.min(s, 60), 7),
    bgEnd: hslToHex(h, Math.min(s, 50), 4),
    surface: hslToHex(h, Math.min(s, 55), 13),
    surfaceLight: hslToHex(h, Math.min(s, 50), 19),
    accent: hslToHex(h, Math.min(s + 10, 70), 30),
    accentLight: hslToHex(h, Math.min(s + 15, 75), 48),
    accentMuted: hslToHex(h, Math.min(s + 5, 65), 24),
    text: hslToHex(h, 25, 93),
    textMuted: hslToHex(h, 15, 60),
    border: hslToHex(h, Math.min(s, 45), 23),
  };
};

// Compute all CSS vars needed by animations as hex strings (no CSS lookup required)
export const computeResolvedVars = (primaryColor) => {
  if (!primaryColor) return {};
  const dark = deriveDarkPalette(primaryColor);
  return {
    '--sa-dark-accent': dark.accent,
    '--sa-dark-accent-light': dark.accentLight,
    '--sa-dark-accent-muted': dark.accentMuted,
    '--sa-dark-surface-light': dark.surfaceLight,
    '--sa-primary': adjustLightness(primaryColor, 12),
    '--sa-primary-soft': adjustLightness(primaryColor, 20),
  };
};

const hexToRgbFloats = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16) / 255,
    parseInt(h.substring(2, 4), 16) / 255,
    parseInt(h.substring(4, 6), 16) / 255,
  ];
};

const rgbFloatsMatch = (a, b, tol = 0.008) =>
  Math.abs(a[0] - b[0]) < tol &&
  Math.abs(a[1] - b[1]) < tol &&
  Math.abs(a[2] - b[2]) < tol;

// Recursively replace fill/stroke colors in a deep-cloned Lottie JSON object.
// colorMap: { '#hexSrc': '#hexDst' }
const replaceColors = (obj, parsedMap) => {
  if (Array.isArray(obj)) {
    return obj.map(v => replaceColors(v, parsedMap));
  }
  if (obj && typeof obj === 'object') {
    // Fill ('fl') and stroke ('st') shape types
    if ((obj.ty === 'fl' || obj.ty === 'st') && obj.c) {
      const c = { ...obj.c };
      if (c.a === 0 && Array.isArray(c.k) && c.k.length >= 3) {
        // Static color — c.k is [r, g, b, a]
        const rgb = [c.k[0], c.k[1], c.k[2]];
        for (const [srcFloats, dstFloats] of parsedMap) {
          if (rgbFloatsMatch(rgb, srcFloats)) {
            c.k = [dstFloats[0], dstFloats[1], dstFloats[2], c.k[3] ?? 1];
            break;
          }
        }
      } else if (c.a === 1 && Array.isArray(c.k)) {
        // Animated keyframes — each keyframe has s (start) and e (end) color arrays
        c.k = c.k.map(kf => {
          if (!kf || typeof kf !== 'object') return kf;
          const out = { ...kf };
          if (Array.isArray(out.s) && out.s.length >= 3) {
            const rgb = [out.s[0], out.s[1], out.s[2]];
            for (const [srcF, dstF] of parsedMap) {
              if (rgbFloatsMatch(rgb, srcF)) {
                out.s = [dstF[0], dstF[1], dstF[2], out.s[3] ?? 1];
                break;
              }
            }
          }
          if (Array.isArray(out.e) && out.e.length >= 3) {
            const rgb = [out.e[0], out.e[1], out.e[2]];
            for (const [srcF, dstF] of parsedMap) {
              if (rgbFloatsMatch(rgb, srcF)) {
                out.e = [dstF[0], dstF[1], dstF[2], out.e[3] ?? 1];
                break;
              }
            }
          }
          return out;
        });
      }
      return { ...obj, c };
    }
    // Gradient fills — color stops are a flat [pos, r, g, b, ...] array in g.k.k
    if (obj.ty === 'gf' && obj.g && obj.g.k && obj.g.k.a === 0 && Array.isArray(obj.g.k.k)) {
      const stops = [...obj.g.k.k];
      const n = obj.g.p || 0;
      for (let i = 0; i < n; i++) {
        const base = i * 4;
        const rgb = [stops[base + 1], stops[base + 2], stops[base + 3]];
        for (const [srcF, dstF] of parsedMap) {
          if (rgbFloatsMatch(rgb, srcF)) {
            stops[base + 1] = dstF[0];
            stops[base + 2] = dstF[1];
            stops[base + 3] = dstF[2];
            break;
          }
        }
      }
      return { ...obj, g: { ...obj.g, k: { ...obj.g.k, k: stops } } };
    }
    // Solid color layers — sc is a hex string on the layer object
    if (typeof obj.sc === 'string' && obj.sc.startsWith('#') && obj.sc.length >= 4) {
      const rgb = hexToRgbFloats(obj.sc);
      for (const [srcF, dstF] of parsedMap) {
        if (rgbFloatsMatch(rgb, srcF)) {
          const newHex = '#' + dstF.slice(0, 3)
            .map(v => Math.round(v * 255).toString(16).padStart(2, '0'))
            .join('');
          return { ...obj, sc: newHex };
        }
      }
    }
    const out = {};
    for (const key of Object.keys(obj)) {
      out[key] = replaceColors(obj[key], parsedMap);
    }
    return out;
  }
  return obj;
};

export const applyBrandColors = (animationData, colorMap, resolvedVars = {}) => {
  // Resolve CSS variable destinations to hex before traversal
  const parsedMap = Object.entries(colorMap).map(([src, dst]) => {
    let resolvedDst;
    if (dst.startsWith('var(')) {
      const varName = dst.slice(4, -1);
      // Prefer pre-computed resolvedVars (from Redux primaryColor), fall back to CSS lookup
      resolvedDst = resolvedVars[varName] || getCssVar(varName);
    } else {
      resolvedDst = dst;
    }
    if (!resolvedDst || resolvedDst.length < 4) return null;
    return [hexToRgbFloats(src), hexToRgbFloats(resolvedDst)];
  }).filter(Boolean);

  return replaceColors(animationData, parsedMap);
};

// Color maps per animation file — keys are EXACT hex values extracted from each JSON
export const PLANED_COLOR_MAP = {
  '#3931ac': 'var(--sa-primary)',
  '#474bd8': 'var(--sa-primary)',
  '#5d68f9': 'var(--sa-primary-soft)',
  '#d0d2d3': 'var(--sa-primary-soft)',
};

export const DATA_SCANNING_COLOR_MAP = {
  '#3e479b': 'var(--sa-dark-accent)',
  '#4658ad': 'var(--sa-dark-accent)',
  '#4c65ba': 'var(--sa-dark-accent-light)',
  '#585baf': 'var(--sa-dark-accent-light)',
  '#599af2': 'var(--sa-dark-accent-light)',
  '#6161b5': 'var(--sa-dark-accent-light)',
  '#9392df': 'var(--sa-primary)',
  '#9396cf': 'var(--sa-primary)',
  '#c7c7ef': 'var(--sa-primary-soft)',
  '#e1e2fe': 'var(--sa-primary-soft)',
  '#ecf1f7': 'var(--sa-primary-soft)',
  '#f28393': 'var(--sa-primary)',
  '#f492a0': 'var(--sa-primary)',
  '#f59fac': 'var(--sa-primary-soft)',
  '#f7a9b4': 'var(--sa-primary-soft)',
};

export const LOADING_FILES_COLOR_MAP = {
  '#b2c1d1': 'var(--sa-primary-soft)',
  '#b6bbc4': 'var(--sa-primary-soft)',
  '#caced5': 'var(--sa-primary)',
  '#d9e3eb': 'var(--sa-primary)',
};


export const MOBILE_FILE_COLOR_MAP = {
  '#e4e6eb': 'var(--sa-primary)',
  '#f0f2f5': 'var(--sa-primary-soft)',
};
